import { useState } from 'react';
import * as Sentry from '@sentry/react';

import type { FactValue, ReviewCategory, ReviewFact } from '@karehero/models';
import { TRIAGE_QUESTION_ID } from '@karehero/models';

import { useDispatch } from 'store';
import { newToast } from 'store/slices/uiSlice';
import { useUpdateIsOnboardedMutation } from 'store/api/iam';
import { useHandleTriageResponsesMutation } from 'store/api/triage';
import type { Who } from 'config/onboardingV4/flows';
import type { TriageQuestion } from 'config/triageQuestions';
import { CONFIRM } from 'config/onboardingV4/copy';
import { optionLabels, type Knowledge } from 'utils/triageKnowledge';
import { phrase, type Person } from 'utils/person';
import { splitFullName } from 'utils/splitFullName';

/** The confirm page's fact for the care recipient's name. */
const NAME_FACT_ID = 'name';

/** The care recipient's name as heard, or as they typed it over what was heard. */
export interface CareRecipientName {
  value?: string;
  isTyped: boolean;
  setTyped: (name: string) => void;
}

export interface UseOnboardingV4ConfirmOptions {
  careCircleId?: string;
  questions: readonly TriageQuestion[];
  knowledge: Knowledge;
  person: Person;
  who?: Who;
  name: CareRecipientName;
  sendAnswers: (question: TriageQuestion, optionIDs: string[]) => void;
  /** Called once the account is marked onboarded. */
  onConfirmed: () => void;
}

/**
 * useOnboardingV4Confirm lays out what was heard and answered for them to
 * check, saves what they change, and finishes onboarding once they confirm.
 */
export const useOnboardingV4Confirm = ({
  careCircleId,
  questions,
  knowledge,
  person,
  who,
  name,
  sendAnswers,
  onConfirmed,
}: UseOnboardingV4ConfirmOptions) => {
  const dispatch = useDispatch();
  const [handleTriageResponses] = useHandleTriageResponsesMutation();
  const [updateIsOnboarded] = useUpdateIsOnboardedMutation();
  const [isFinishing, setIsFinishing] = useState(false);

  const nameFact = (): ReviewFact[] =>
    who === 'someone-else'
      ? [
          {
            id: NAME_FACT_ID,
            label: CONFIRM.nameLabel,
            value: name.value,
            status: name.isTyped
              ? 'confirmed'
              : name.value
                ? 'inferred'
                : 'missing',
          },
        ]
      : [];

  const questionFact = (q: TriageQuestion): ReviewFact => {
    const answered = knowledge.answers[q.id];
    const known = answered ?? knowledge.predicted[q.id];
    return {
      id: q.id,
      label: q.label,
      value: known ? optionLabels(q, known) : undefined,
      status: answered ? 'confirmed' : known ? 'inferred' : 'missing',
      control: 'choice',
      choices: q.options.map((o) => ({ value: o.id, label: o.label })),
      selected: known,
    };
  };

  const categories: ReviewCategory[] = [
    {
      label: phrase(CONFIRM.title, person),
      facts: [...nameFact(), ...questions.map(questionFact)],
    },
  ];

  const saveFact = (factID: string, value: FactValue) => {
    if (factID === NAME_FACT_ID) {
      if (typeof value === 'string') name.setTyped(value.trim());
      return;
    }
    const q = questions.find((candidate) => candidate.id === factID);
    if (!q) return;
    // A choice saves the option's id; the control offers no other value.
    const chosen = (Array.isArray(value) ? value : [value]).filter((id) =>
      q.options.some((o) => o.id === id),
    );
    if (chosen.length > 0) sendAnswers(q, chosen);
  };

  /** Saves the care recipient's name as heard or typed, best effort. */
  const saveName = async () => {
    if (!name.value || !careCircleId || who !== 'someone-else') return;
    const { firstName, lastName } = splitFullName(name.value);
    const res = await handleTriageResponses({
      careCircleID: careCircleId,
      responses: [
        {
          questionID: TRIAGE_QUESTION_ID.careRecipientFirstName,
          response: firstName,
        },
        {
          questionID: TRIAGE_QUESTION_ID.careRecipientLastName,
          response: lastName,
        },
      ],
    });
    // A name not saved can be added on the care profile; onboarding still finishes.
    if ('error' in res) Sentry.captureException(res.error);
  };

  const confirm = async () => {
    if (isFinishing) return;
    setIsFinishing(true);
    // What was only read from what they said is theirs once they confirm it.
    questions.forEach((q) => {
      const predicted = knowledge.predicted[q.id];
      if (predicted && !knowledge.answers[q.id]) sendAnswers(q, predicted);
    });
    await saveName();

    const res = await updateIsOnboarded();
    if ('error' in res) {
      Sentry.captureException(
        new Error('Failed to mark the account onboarded'),
      );
      dispatch(
        newToast({
          title: 'Something went wrong',
          description:
            'We could not finish setting up your account. Please try again.',
          variant: 'error',
        }),
      );
      setIsFinishing(false);
      return;
    }
    onConfirmed();
  };

  return { categories, saveFact, confirm, isFinishing };
};
